import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { apiGet } from '@/api';
import {
    Server, AlertCircle, CheckCircle2, Clock, Lock, Shield, Zap
} from 'lucide-react';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import DirectSourcePanel from '@/components/relay/DirectSourcePanel';

// ── Types ──────────────────────────────────────────────────────────────────────

interface RelayAgent {
    id: string; name: string; description?: string;
    status: 'ACTIVE' | 'IDLE' | 'PENDING' | 'REVOKED' | string;
    version?: string; heartbeat_at?: string; tenant_id?: string;
}

interface RelaySourceConfigProps {
    node: any;
    onChangeData: (key: string | Record<string, any>, value?: any) => void;
}

const STATUS_STYLES: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
    ACTIVE:  { variant: 'default',     icon: CheckCircle2 },
    IDLE:    { variant: 'secondary',   icon: Clock },
    PENDING: { variant: 'outline',     icon: Clock },
    REVOKED: { variant: 'destructive', icon: AlertCircle },
};

// ── Component ─────────────────────────────────────────────────────────────────

export const RelaySourceConfig = ({ node, onChangeData }: RelaySourceConfigProps) => {
    const { formatDate } = useTimezoneFormatter();
    const data = node.data || {};

    const [agents,      setAgents]      = useState<RelayAgent[]>([]);
    const [agentLoading, setAgentLoading] = useState(false);
    const [agentError,   setAgentError]   = useState<string | null>(null);

    const selectedAgent = agents.find(a => a.id === data.agentId);

    // ── Load agents ───────────────────────────────────────────────────────────
    useEffect(() => {
        setAgentLoading(true);
        apiGet('/relay/agents')
            .then((res: any) => Array.isArray(res) && setAgents(res.filter((a: RelayAgent) => a.status !== 'REVOKED')))
            .catch(() => setAgentError('Could not load relay agents.'))
            .finally(() => setAgentLoading(false));
    }, []);

    // ── Derived ready state ───────────────────────────────────────────────────
    const directCfg    = data.directConfig || {};
    const tableConfig  = directCfg.tables  || {};
    const allIncluded  = Object.entries(tableConfig).filter(([, v]: any) => v?.included !== false);
    const pdTables     = allIncluded.filter(([, v]: any) => Object.values((v as any).columns || {}).some((c: any) => c?.identifier));
    const isReady      = !!data.agentId && !!directCfg.datasourceId && allIncluded.length > 0;

    return (
        <div className="space-y-5">

            {/* ── 1. Agent Selector ────────────────────────────────────────── */}
            <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
                    <Server className="h-3.5 w-3.5 text-blue-500" /> Relay Agent
                </label>

                {agentError && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />{agentError}
                    </div>
                )}

                <Select
                    value={data.agentId || ''}
                    onValueChange={(agentId: string) => {
                        const agent = agents.find(a => a.id === agentId);
                        onChangeData({ agentId, agentName: agent?.name || '' });
                    }}
                    disabled={agentLoading || agents.length === 0}
                >
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder={
                            agentLoading ? 'Loading agents…'
                            : agents.length === 0 ? 'No active agents found'
                            : 'Select a Relay Agent…'
                        } />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[100] shadow-lg border border-gray-200">
                        {agents.map(agent => {
                            const style = STATUS_STYLES[agent.status] || STATUS_STYLES['IDLE'];
                            const Icon = style.icon;
                            return (
                                <SelectItem key={agent.id} value={agent.id}>
                                    <div className="flex items-center gap-2">
                                        <Icon className="h-3 w-3 text-gray-400 shrink-0" />
                                        <span>{agent.name}</span>
                                        <Badge variant={style.variant} className="text-[9px] px-1 py-0 ml-auto">
                                            {agent.status}
                                        </Badge>
                                    </div>
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>

                {selectedAgent && (
                    <div className="p-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                        <div>
                            <span className="text-[11px] font-medium text-blue-900">{selectedAgent.name}</span>
                            {selectedAgent.description && (
                                <p className="text-[10px] text-blue-600 mt-0.5">{selectedAgent.description}</p>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <Badge variant={STATUS_STYLES[selectedAgent.status]?.variant || 'secondary'} className="text-[9px]">
                                {selectedAgent.status}
                            </Badge>
                            <div className="flex gap-2 text-[9px] text-blue-400">
                                {selectedAgent.version && <span>v{selectedAgent.version}</span>}
                                {selectedAgent.heartbeat_at && <span>seen {formatDate(selectedAgent.heartbeat_at)}</span>}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Divider ──────────────────────────────────────────────────── */}
            <div className="border-t border-gray-100" />

            {/* ── 2. Direct Source Panel ────────────────────────────────────── */}
            <DirectSourcePanel
                directConfig={directCfg}
                onChange={(cfg) => onChangeData('directConfig', cfg)}
            />

            {/* ── 3. Ready summary ─────────────────────────────────────────── */}
            {isReady && (
                <div className="p-2.5 bg-green-50 border border-green-100 rounded-lg space-y-1.5">
                    <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wider">✅ Ready to Run</p>
                    <div className="text-[10px] text-gray-600 space-y-0.5">
                        <div>Agent: <span className="font-medium text-gray-900">{data.agentName || data.agentId}</span></div>
                        <div className="flex gap-3">
                            <span className="flex items-center gap-1 text-red-600">
                                <Shield size={9} />
                                {pdTables.length} PD table{pdTables.length !== 1 ? 's' : ''} → Relay Agent
                            </span>
                            <span className="flex items-center gap-1 text-green-600">
                                <Zap size={9} />
                                {allIncluded.length - pdTables.length} → DuckDB
                            </span>
                        </div>
                        {pdTables.length > 0 && (
                            <div className="flex items-center gap-1 text-red-600 font-mono text-[9px]">
                                <Lock size={9} /> {pdTables.map(([n]) => n).join(', ')}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RelaySourceConfig;
